import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useFsAdjustStore, useAdjustDraftStore, useAdjustmentStore } from '@/store/audit';
import { fetchAdjustMeta } from '@/store/audit/auditMeta';
import { getWorkingPaper } from '@/services/auditService/auditWorkingPaperService';
import { isWpMetadataOnly, WP_METADATA_ONLY_KEYS, extractWpMetadata, isWpPrimaryRowsEmpty, seedWpEmptyPrimaryRowsFromFactory, ensureCommonInvestmentTemplateRows } from '@/adjustment/adjust/dialogs/utils/initial-data-factories';
import { resolveWpFetchCode } from '@/adjustment/adjust/dialogs/utils/wpSpecialSyncCodes';
import { useWpPeriodScopeContext } from '@/adjustment/hooks/WpPeriodScopeContext';
import { resolvePriorPeriodSlice } from '@/utils/wpPriorPeriodBfRollForward';
import { computeYearOfAssessment } from '@/utils/dataUtils';
import { getActiveWpDataMode } from '@/services/auditService/wpPeriodYoaBridge';
import {
  getWpYoaMapCache,
  projectYoaBucketToWorkingPaperData,
  upsertWpYoaBucketDetails,
} from '@/services/auditService/wpYoaMapCache';
import { resolveWpDataMode, wpDataModeNeedsYoa, wpDataModeUsesBatchSave } from '@/utils/auditPeriodWpMulti';
import {
  isPlTotalViewSliceKey,
  loadAndAggregatePlWpDetails,
  PL_TOTAL_VIEW_SLICE_KEY,
} from '@/pages/components/fs-report/plMultiTotalView';
import {withPlYoaClientOnLine} from '@/adjustment/hooks/plYoaClientAmounts';

/** P7 §2.4: only PL·MULTI / BS whitelist attach yearOfAssessment on get */
function resolveYoaForWpGet(code: string | undefined | null, periodYoa: string | null | undefined, sliceKey?: string | null): string | undefined {
  // TotalSingle preview: never fetch a real YOA bucket
  if (isPlTotalViewSliceKey(sliceKey)) return undefined;
  const spanHint = periodYoa ? 'MULTI' : 'SINGLE';
  const bridged = getActiveWpDataMode();
  const mode = bridged !== 'single' ? bridged : resolveWpDataMode(code || '', spanHint);
  if (!wpDataModeNeedsYoa(mode)) return undefined;
  const trimmed = typeof periodYoa === 'string' ? periodYoa.trim() : '';
  return trimmed || undefined;
}

/**
 * 将 initialAdjustment 注入到后端数据指定行（默认首行）的 adjustment 字段
 * @param backendData 后端返回的数据
 * @param initialAdjustment 要注入的调整值
 * @param targetRowIndex 目标行索引（仅作用于 commonRow；缺省为 0）
 * @returns 注入后的数据
 */
function injectAdjustmentToTargetRow(
  backendData: any,
  initialAdjustment: number,
  targetRowIndex: number = 0,
): any {
  if (!backendData) return backendData;
  const rowIndex = Number.isFinite(targetRowIndex) && targetRowIndex >= 0 ? targetRowIndex : 0;

  const injectCommonRow = (commonRow: any[]): any[] =>
    commonRow.map((row: any, index: number) => {
      if (index === rowIndex) {
        return { ...row, adjustment: initialAdjustment };
      }
      return row;
    });

  // 处理数组格式: [{ commonRow: [...] }]
  if (Array.isArray(backendData) && backendData.length > 0) {
    const firstItem = backendData[0];
    if (firstItem && firstItem.commonRow && Array.isArray(firstItem.commonRow) && firstItem.commonRow.length > 0) {
      const updatedFirstItem = {
        ...firstItem,
        commonRow: injectCommonRow(firstItem.commonRow),
      };
      return [updatedFirstItem, ...backendData.slice(1)];
    }
    return backendData;
  }

  // 处理对象格式: { commonRow: [...] }
  if (backendData && typeof backendData === 'object' && backendData.commonRow && Array.isArray(backendData.commonRow) && backendData.commonRow.length > 0) {
    return {
      ...backendData,
      commonRow: injectCommonRow(backendData.commonRow),
    };
  }

  return backendData;
}

/**
 * GET 无有效 commonRow（空 details / 缺失 / 仅元数据壳）时走 initialDataFactory。
 * WHY: details=[] 合并后常带 outstanding/issueStatus，旧 isWpMetadataOnly 判非空，BS-CL-DP 三行模板不落地。
 */
function isFixedCommonRowScheduleDetailsEmpty(code: string | undefined | null, backendData: any): boolean {
  const upper = String(code || '').toUpperCase();
  // BS-EQ-AL：单行父级 factory；BS-CL-DP：固定三行 Dividend payable schedule
  if (upper !== 'BS-EQ-AL' && upper !== 'BS-CL-DP') return false;
  if (!backendData) return true;
  if (typeof backendData !== 'object') return true;

  // 兼容尚未 flatten 的 { details: [] | [...] }
  if (Array.isArray(backendData.details) && !('commonRow' in backendData)) {
    if (backendData.details.length === 0) return true;
    const first = backendData.details[0];
    if (!first || typeof first !== 'object' || Array.isArray(first)) return true;
    const nestedRows = (first as any).commonRow;
    return !Array.isArray(nestedRows) || nestedRows.length === 0;
  }

  const rows = backendData.commonRow;
  return !Array.isArray(rows) || rows.length === 0;
}

/**
 * 空数据 factory 补齐 item/perClient 后，将 GET stub 的 adjustments 挂到首行供 CommonWp 渲染。
 * 用于 BS-EQ-AL / BS-CA-DPP（backendManaged）等「行空但有 adjustment」路径。
 */
function mergeEmptyFactoryWithAdjustments(factoryData: any, backendData: any): any {
  const adjustments = Array.isArray(backendData?.adjustments)
    ? backendData.adjustments
    : (Array.isArray(backendData?.details?.[0]?.adjustments)
      ? backendData.details[0].adjustments
      : null);
  if (!factoryData || typeof factoryData !== 'object' || !Array.isArray(adjustments) || adjustments.length === 0) {
    return factoryData;
  }
  const commonRow = Array.isArray(factoryData.commonRow) ? factoryData.commonRow : [];
  if (commonRow.length === 0) {
    return {...factoryData, adjustments};
  }
  return {
    ...factoryData,
    adjustments,
    commonRow: commonRow.map((row: any, idx: number) => (
      idx === 0 ? {...row, adjustments} : row
    )),
  };
}

export interface UseAdjustDialogDataOptions {
  open: boolean;
  annualId: number;
  code: string;
  companyId?: number;
  isControlled?: boolean;
  parDetail?: any;
  setParDetail?: (data: any) => void;
  line?: any;
  initialDataFactory: (line: any, initialAdjustment?: number) => any;
  initialAdjustment?: number;
  preferBackendData?: boolean; // chip-open：每次打开优先 GET，覆盖草稿脏读；受控/fromPaper 应传 false
  isToPaper?: boolean; // 新增：是否是 toPaper 组件，只有 toPaper 才注入 initialAdjustment
  /** toPaper 注入 adjustment 的目标行索引（如 TaxWp 按 FS code 映射到指定 commonRow） */
  adjustmentTargetRowIndex?: number;
  workflow?: 'audit' | 'account'; // 工作流类型，account 时后端额外返回 gl-invoice 数据
  /**
   * 行数据完全由后端 WP 管理（如 BS-CA-DPP Deposit paid）：
   * 始终请求后端；空结果不落本地草稿/FS 兜底，只渲染接口数据。
   */
  backendManagedRows?: boolean;
  /**
   * Phase 4: MULTI PL Tab 子期间。缺省 = 整段 annual（SINGLE / 现网）。
   * 覆盖 meta.periodFrom/To/yoa；草稿键附加 sliceKey 避免跨年串数据。
   */
  periodScope?: {
    periodFrom: string;
    periodTo: string;
    yearOfAssessment?: string | null;
    sliceKey?: string;
    /** false → do not load shared company-annual WP into this tab (isolates rows / ppeRate) */
    isFirstSlice?: boolean;
  } | null;
}

function deepCloneDetail<T>(value: T): T {
  if (value == null || typeof value !== 'object') return value;
  try {
    return structuredClone(value);
  } catch {
    return JSON.parse(JSON.stringify(value)) as T;
  }
}

/** Drop shared DB identity so MULTI tabs do not overwrite each other's ppeRate on save. */
function isolateSliceOwnedFields(detail: any): any {
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return detail;
  const next = deepCloneDetail(detail);
  if (next.ppeRate && typeof next.ppeRate === 'object') {
    const {id: _id, annualId: _annualId, ...rateRest} = next.ppeRate;
    next.ppeRate = rateRest;
  }
  return next;
}

export interface DialogMetaData {
  yoa: string | null;
  lastYoa: string | null;
  currency: string;
  categoryLine: any;
  periodTo: string | null;
  periodFrom: string | null;
  paperDetail: any[] | null;
}

export interface BackendIds {
  headerId: string | undefined;
  lineId: number | undefined;
}

/**
 * 新的 Dialog 数据初始化 Hook
 * 
 * 功能：
 * 1. 根据 mode 决定数据获取逻辑
 *    - 在 adjustMode 下：先调用 queryAdjustRecordByWorking，有数据则使用，无数据则从父组件获取
 *    - 不在 adjustMode 下：调用 queryAdjustRecordByWorking，设置 readOnly 模式
 * 2. 自动加载 meta 数据（yoa, currency 等）
 * 3. 提供统一的数据更新接口
 * 
 * @example
 * ```typescript
 * const { meta, currentData, updateData, isReadOnly, initialized } = useAdjustDialogData({
 *   open,
 *   annualId,
 *   companyId,
 *   scheduleCode,
 *   mode: payload?.mode,
 *   initialDataFactory: (line, initialAdj) => ({
 *     rows: [{ perClient: line?.amount || 0, adjustment: initialAdj || 0 }]
 *   }),
 * });
 * ```
 */
// 元数据判定使用统一的 isWpMetadataOnly / WP_METADATA_ONLY_KEYS（来自 initial-data-factories）

export function useAdjustDialogData(options: UseAdjustDialogDataOptions) {
  const {
    open,
    annualId,
    companyId,
    code,
    isControlled = false,
    parDetail,
    setParDetail,
    line: propLine,
    initialDataFactory,
    initialAdjustment,
    preferBackendData = true,
    isToPaper = false,
    adjustmentTargetRowIndex = 0,
    workflow,
    backendManagedRows = false,
    periodScope: periodScopeOption = null,
  } = options;

  const periodScopeFromCtx = useWpPeriodScopeContext();
  // Explicit option wins; else DialogLauncher context (covers all WPs without per-file wiring)
  const periodScope = periodScopeOption ?? periodScopeFromCtx ?? null;

  // Use getState() pattern to avoid unstable store references in dependency arrays
  // This prevents infinite re-renders caused by zustand store subscriptions
  const getLineByCode = useFsAdjustStore((state) => state.getLineByCode);
  const periodsFromStore = useFsAdjustStore((state) => state.adjustRecord?.periods);
  const headerCompanyId = useAdjustmentStore((state) => state.header?.companyId);
  
  // Get stable function references from adjustDraftStore
  const getUnifiedDetail = useAdjustDraftStore((state) => state.getUnifiedDetail);
  const setUnifiedDetail = useAdjustDraftStore((state) => state.setUnifiedDetail);

  // SINGLE / no scope: draft key === code (#20). MULTI PL: isolate by sliceKey.
  const draftDetailKey = useMemo(() => {
    const slice = periodScope?.sliceKey;
    if (slice && code) return `${code}__${slice}`;
    return code;
  }, [code, periodScope?.sliceKey]);

  // Meta 数据状态
  const [meta, setMeta] = useState<DialogMetaData>({
    yoa: null,
    lastYoa: null,
    currency: '',
    categoryLine: null,
    periodTo: null,
    periodFrom: null,
    paperDetail: null,
  });
  
  // 内部数据状态
  const [internalData, setInternalData] = useState<any>(null);
  
  // 初始化标志
  const [initialized, setInitialized] = useState(false);

  // Track special case where backend returns an empty list ([]) for working paper
  const [backendListEmpty, setBackendListEmpty] = useState(false);
  
  // 存储后端返回的 headerId 和 lineId
  const [backendIds, setBackendIds] = useState<BackendIds>({
    headerId: undefined,
    lineId: undefined,
  });
  
  // 防止重复初始化
  const initCodeRef = useRef<string | null>(null);
  const initialDataFactoryRef = useRef(initialDataFactory);
  const parDetailRef = useRef(parDetail);
  const initialAdjustmentRef = useRef(initialAdjustment);
  const internalDataRef = useRef<any>(null);

  useEffect(() => {
    initialDataFactoryRef.current = initialDataFactory;
    parDetailRef.current = parDetail;
    initialAdjustmentRef.current = initialAdjustment;
  }, [initialDataFactory, parDetail, initialAdjustment]);

  useEffect(() => {
    internalDataRef.current = internalData;
  }, [internalData]);

  // 获取 line 数据
  const lineData = useMemo(() => {
    // 优先使用传入的 line 数据
    if (propLine) {
      return propLine;
    }
    // 其次从 store 获取
    if (code) {
      const storeLine = getLineByCode(code);
      if (storeLine) {
        return storeLine;
      }
    }
    // 最后使用 meta 中的 categoryLine
    if (meta.categoryLine) {
      return meta.categoryLine;
    }
    return null;
  }, [propLine, code, meta.categoryLine, getLineByCode]);
  
  // 获取 companyId（从 adjustmentStore 或参数）
  const effectiveCompanyId = useMemo(() => {
    return companyId || headerCompanyId || 0;
  }, [companyId, headerCompanyId]);
  
  // 步骤1: 加载 meta 数据
  // Phase 6: period Tab 切换只更新期间字段，不把 initialized 打回 false（龙骨仅首开/单年）
  useEffect(() => {
    let cancelled = false;
    
    if (!open) {
      // Close handled in step 2 (full reset). Do not touch initialized here on period flickers.
      return;
    }
    
    if (!annualId || !code) {
      // Don't reset initialized when dialog is open but code/annualId missing.
      // Step 2 handles initialization for these cases (e.g. BankLoan/VehicleLoan
      // which use their own fetch logic with hardcoded codes).
      return;
    }
    
    (async () => {
      try {
        const result = await fetchAdjustMeta(annualId, code, effectiveCompanyId, true);
        if (cancelled) return;

        // MULTI non-first Tab: Last Year YOA = prior slice (e.g. 2025/26 → 2024/25),
        // not annual lastYearPeriodTo (year before whole multi span, e.g. 2023/24).
        const priorSlice = resolvePriorPeriodSlice(periodsFromStore, periodScope?.sliceKey);
        const lastYoa = priorSlice
          ? (priorSlice.yearOfAssessment
            ?? computeYearOfAssessment(priorSlice.periodTo)
            ?? result.lastYearOfAssessment)
          : result.lastYearOfAssessment;

        setMeta({
          yoa: periodScope?.yearOfAssessment ?? result.yearOfAssessment,
          lastYoa,
          currency: result.currency,
          categoryLine: result.line,
          periodTo: periodScope?.periodTo ?? result.periodTo,
          periodFrom: periodScope?.periodFrom ?? result.periodFrom,
          paperDetail: result.paperDetail,
        });
      } catch (error) {}
    })();
    
    return () => {
      cancelled = true;
    };
  }, [open, annualId, code, effectiveCompanyId, periodScope?.periodFrom, periodScope?.periodTo, periodScope?.yearOfAssessment, periodScope?.sliceKey, periodsFromStore]);
  
  // 步骤2: 初始化数据
  // 使用 ref 追踪是否已经初始化，避免重复初始化
  const hasInitializedRef = useRef(false);
  
  useEffect(() => {
    if (!open) {
      initCodeRef.current = null;
      hasInitializedRef.current = false;
      setInitialized(false);
      setBackendIds({ headerId: undefined, lineId: undefined });
      setBackendListEmpty(false);
      // Drop in-memory WP so Cancel → reopen cannot reuse the first-open snapshot
      if (!isControlled) {
        setInternalData(null);
      }
      return;
    }
    
    // 如果已经初始化过，不再重复初始化（避免 FsSelectionDrawer 打开时清空数据）
    if (hasInitializedRef.current && initCodeRef.current === draftDetailKey) {
      return;
    }

    // Phase 6: MULTI Period Tab 切换 — 同一 Dialog 会话内换片，禁止龙骨
    const seamlessPeriodSwitch =
      hasInitializedRef.current
      && !!periodScope?.sliceKey
      && !!initCodeRef.current
      && initCodeRef.current !== draftDetailKey;

    let isCancelled = false;

    (async () => {
      try {
        const draftStore = useAdjustDraftStore.getState();

        if (seamlessPeriodSwitch) {
          const prevKey = initCodeRef.current as string;
          // Persist current slice edits into draft bucket (deep clone — no shared refs across tabs)
          const currentSnap = isControlled
            ? (parDetailRef.current || draftStore.getUnifiedDetail(prevKey))
            : internalDataRef.current;
          if (currentSnap != null) {
            draftStore.setUnifiedDetail(prevKey, deepCloneDetail(currentSnap));
          }

          // Prefer already-loaded draft for target slice (instant, no Skeleton)
          const cached = draftStore.getUnifiedDetail(draftDetailKey);
          const hasCached = cached && typeof cached === 'object' && Object.keys(cached).length > 0;

          if (hasCached) {
            const isolated = deepCloneDetail(cached);
            if (isControlled && setParDetail) {
              setParDetail(isolated);
            } else {
              setInternalData(isolated);
            }
            initCodeRef.current = draftDetailKey;
            hasInitializedRef.current = true;
            // Keep initialized===true → no Skeleton (#P6)
            setInitialized(true);
            return;
          }

          // First visit to this slice in-session: load quietly without flipping initialized
          // (fall through to fetch below with skipSkeleton=true)
        }

        let initialData;
        let backendData = null;
        let extractedHeaderId: number | undefined = undefined;
        let extractedLineId: number | undefined = undefined;
        let didFetchBackend = false;
        let isBackendListEmptyLocal = false;
        
        // 使用 getState() 获取 store 数据，避免依赖 store 引用变化
        const currentParDetail = parDetailRef.current;
        const codeToUse = code || lineData?.code;
        const apiCode = codeToUse ? resolveWpFetchCode(codeToUse) : codeToUse;

        const skipSkeleton = seamlessPeriodSwitch;
        /** MULTI Period Tab isolation only (#20: SINGLE unchanged) */
        const isMultiSliceSession = !!periodScope?.sliceKey;
        const isPlTotalView = isPlTotalViewSliceKey(periodScope?.sliceKey);

        // ---------- TotalSingle (§6.3): aggregate WP detail inside dialog data layer ----------
        // Uses existing GET ?yoaMap=true (BS MULTI wire) + client Σ; no new backend Σ API.
        // All paper-registry components that use this hook receive aggregated currentData.
        if (isPlTotalView && effectiveCompanyId > 0 && annualId > 0 && apiCode) {
          const cachedTotal = draftStore.getUnifiedDetail(draftDetailKey);
          const hasCachedTotal = !!(
            cachedTotal
            && typeof cachedTotal === 'object'
            && Object.keys(cachedTotal).length > 0
          );

          let totalData: any;
          if (hasCachedTotal) {
            totalData = deepCloneDetail(cachedTotal);
          } else {
            const slices = (Array.isArray(periodsFromStore) ? periodsFromStore : [])
              .filter((p: any) => p && p.key)
              .map((p: any) => ({
                key: String(p.key),
                yearOfAssessment: p.yearOfAssessment ?? null,
              }));
            const aggregated = await loadAndAggregatePlWpDetails({
              code: apiCode,
              slices: slices.length > 0
                ? slices
                : [{key: PL_TOTAL_VIEW_SLICE_KEY, yearOfAssessment: null}],
              companyId: effectiveCompanyId,
              annualId,
              workflow,
            });
            totalData = aggregated
              || initialDataFactoryRef.current(lineData, initialAdjustmentRef.current || 0);
          }

          if (!isCancelled) {
            if (isControlled && setParDetail) {
              setParDetail(totalData);
            } else {
              setInternalData(totalData);
            }
            // Session-only preview draft (code__PL_TOTAL) — never a real YOA bucket
            draftStore.setUnifiedDetail(draftDetailKey, totalData);
            setBackendIds({ headerId: undefined, lineId: undefined });
            initCodeRef.current = draftDetailKey;
            hasInitializedRef.current = true;
            setInitialized(true);
          }
          return;
        }

        // ---------- BS-CA-DPP 等：行数据仅信任 WP 接口（独立分支，不影响下方原逻辑） ----------
        if (backendManagedRows) {
          if (effectiveCompanyId > 0 && annualId > 0 && apiCode) {
            try {
              const response = await getWorkingPaper({
                companyId: effectiveCompanyId,
                annualId: annualId,
                code: apiCode,
                workflow: workflow,
                yearOfAssessment: resolveYoaForWpGet(apiCode, periodScope?.yearOfAssessment, periodScope?.sliceKey),
              });
              didFetchBackend = true;
              if (response && Array.isArray(response) && response.length > 0) {
                backendData = response[0];
              } else if (response && Array.isArray(response) && response.length === 0) {
                backendData = null;
                isBackendListEmptyLocal = true;
              } else if (response && !Array.isArray(response)) {
                backendData = response;
              }
            } catch (err) {
              console.error('Failed to query adjust record:', err);
            }
          }

          // 无有效 commonRow（含仅 {adjustments} stub）视为空：走 factory 再挂 adjustments
          const isBackendDataEmpty = isWpMetadataOnly(backendData) ||
            (backendData != null && typeof backendData === 'object' && !Array.isArray(backendData)
              && (!Array.isArray((backendData as any).commonRow)
                || (backendData as any).commonRow.length === 0));
          if (isBackendDataEmpty) {
            isBackendListEmptyLocal = true;
          }
          if (!isCancelled && didFetchBackend) {
            setBackendListEmpty(isBackendListEmptyLocal);
          }

          if (backendData && !isBackendDataEmpty) {
            const currentInitialAdjustment = initialAdjustmentRef.current;
            if (isToPaper && currentInitialAdjustment !== undefined && currentInitialAdjustment !== null && currentInitialAdjustment !== 0) {
              initialData = injectAdjustmentToTargetRow(backendData, currentInitialAdjustment, adjustmentTargetRowIndex);
            } else {
              initialData = backendData;
            }
          } else {
            initialData = initialDataFactoryRef.current(lineData, initialAdjustmentRef.current || 0);
            if (initialData && typeof initialData === 'object' && !Array.isArray(initialData)) {
              Object.assign(initialData, extractWpMetadata(backendData));
            }
            // BS-CA-DPP：空壳 GET 仍可能带 adjustments[]，挂到 factory 首行（对齐 BS-EQ-AL）
            initialData = mergeEmptyFactoryWithAdjustments(initialData, backendData);
            const currentInitialAdjustment = initialAdjustmentRef.current;
            if (isToPaper && currentInitialAdjustment !== undefined && currentInitialAdjustment !== null && currentInitialAdjustment !== 0) {
              initialData = injectAdjustmentToTargetRow(initialData, currentInitialAdjustment, adjustmentTargetRowIndex);
            }
          }

          if (!isCancelled) {
            if (isControlled && setParDetail) {
              setParDetail(initialData);
            } else {
              setInternalData(initialData);
            }
            // 同步清空/覆盖该 code 的草稿，避免下次打开仍读到旧 FS 兜底
            draftStore.setUnifiedDetail(draftDetailKey, initialData);
            setBackendIds({ headerId: extractedHeaderId, lineId: extractedLineId });
            initCodeRef.current = draftDetailKey;
            hasInitializedRef.current = true;
            if (!skipSkeleton) {
              setInitialized(true);
            } else {
              setInitialized(true); // stay true; never flash Skeleton on Tab switch
            }
          }
          return;
        }

        // ---------- 原有逻辑：本地优先 → 后端 → factory ----------
        // toPaper / 受控：必须保留草稿，不能用 GET 盖掉正在组装的调整。
        // 独立打开 WP（preferBackendData）：Cancel 后再开必须 GET，
        // 否则会继续用第一次打开写进 draft 的旧快照（自动保存已落库也看不到）。
        const reuseLocalDraft = !preferBackendData || isToPaper || isControlled;

        // Chip-open（preferBackendData 且非受控）：每次 Dialog 打开优先 GET，避免 Save 后草稿脏读
        // （多数 WP 已传 preferBackendData:!isControlled；此前参数未生效）
        // 受控 AdjustTabs / seamless Tab 切换：保持本地优先，不在此强制刷后端
        const preferBackendOnOpen =
          preferBackendData && !isControlled && !seamlessPeriodSwitch;

        const existingLocalData = seamlessPeriodSwitch
          ? (() => {
              const fromDraft = draftStore.getUnifiedDetail(draftDetailKey);
              return (fromDraft
                && typeof fromDraft === 'object'
                && Object.keys(fromDraft).length > 0)
                ? fromDraft
                : null;
            })()
          : (reuseLocalDraft
            ? (currentParDetail || draftStore.getUnifiedDetail(draftDetailKey))
            : null);
        const hasValidLocalData = !!(existingLocalData
          && typeof existingLocalData === 'object'
          && Object.keys(existingLocalData).length > 0);

        // MULTI: fetch with yearOfAssessment (P7) — no longer skip non-first Tab backend GET
        
        // 1. 尝试获取后端数据
        // ✅ 无本地数据时必拉；chip-open preferBackend 时即使有草稿也重拉（覆盖脏草稿）
        // P8：bs-multi seamless Tab — 优先从 YOA Map 缓存投影；chip 重开则跳过内存缓存走 GET
        if ((!hasValidLocalData || preferBackendOnOpen) && effectiveCompanyId > 0 && annualId > 0 && apiCode) {
          const bsMulti = wpDataModeUsesBatchSave(
            getActiveWpDataMode() !== 'single'
              ? getActiveWpDataMode()
              : resolveWpDataMode(apiCode, periodScope?.yearOfAssessment ? 'MULTI' : 'SINGLE'),
          );
          const yoaMapCache = bsMulti
            ? getWpYoaMapCache(effectiveCompanyId, annualId, apiCode)
            : null;
          // Seamless Tab: reuse YOA Map from first GET even when preferBackendData
          // would otherwise force a second getWorkingPaper for the next slice.
          if (
            !preferBackendOnOpen
            && yoaMapCache
            && (reuseLocalDraft || seamlessPeriodSwitch)
            && periodScope?.yearOfAssessment
            && yoaMapCache.byYearOfAssessment[String(periodScope.yearOfAssessment).trim()]
          ) {
            backendData = projectYoaBucketToWorkingPaperData(
              yoaMapCache,
              periodScope.yearOfAssessment,
            );
            didFetchBackend = true;
          } else {
          try {
            // bs-multi → yoaMap（details=YOA Map）；其它 MULTI 带片级 YOA
            const response = await getWorkingPaper({
              companyId: effectiveCompanyId,
              annualId: annualId,
              code: apiCode,
              workflow: workflow,
              yearOfAssessment: resolveYoaForWpGet(apiCode, periodScope?.yearOfAssessment, periodScope?.sliceKey),
            });
            
            didFetchBackend = true;

            // 新 API 返回数组，取第一个元素作为 detail 数据
            if (response && Array.isArray(response) && response.length > 0) {
              backendData = response[0];
              isBackendListEmptyLocal = false;
            } else if (response && Array.isArray(response) && response.length === 0) {
              // Special case: backend has no records at all
              backendData = null;
              isBackendListEmptyLocal = true;
            } else if (response && !Array.isArray(response)) {
              backendData = response;
              isBackendListEmptyLocal = false;
            }
          } catch (err) {
            console.error('Failed to query adjust record:', err);
          }
          }
        }

        // 2. 决定数据来源
        // 判断后端数据是否为空（或仅含附加元数据而无实际行数据）——原逻辑保持不变
        const isBackendDataEmpty = isWpMetadataOnly(backendData) ||
                                   (backendData && typeof backendData === 'object' && 'commonRow' in backendData && (!backendData.commonRow || (backendData as any).commonRow.length === 0));
        // BS-EQ-AL / BS-CL-DP：GET 无有效 commonRow 时走 CommonWp initialDataFactory
        const isFixedScheduleDetailsEmpty = isFixedCommonRowScheduleDetailsEmpty(codeToUse || code, backendData);
        const useEmptyFactory = isBackendDataEmpty || isFixedScheduleDetailsEmpty;

        // ✅ 当后端仅返回元数据时，也视为 backendListEmpty，确保 EmptyConfirmDialog 等逻辑正确触发
        if (useEmptyFactory) {
          isBackendListEmptyLocal = true;
        }

        if (!isCancelled && didFetchBackend) {
          setBackendListEmpty(isBackendListEmptyLocal);
        }

        const applyBackendOrFactory = () => {
          if (backendData && !useEmptyFactory) {
            const currentInitialAdjustment = initialAdjustmentRef.current;
            if (isToPaper && currentInitialAdjustment !== undefined && currentInitialAdjustment !== null && currentInitialAdjustment !== 0) {
              initialData = injectAdjustmentToTargetRow(backendData, currentInitialAdjustment, adjustmentTargetRowIndex);
            } else {
              initialData = backendData;
            }
            if (isMultiSliceSession) {
              initialData = deepCloneDetail(initialData);
            }
            return;
          }
          // PL MULTI: factory line must carry this YOA Tab's Client (not first-tab copy)
          const factoryLine = isMultiSliceSession
            && String(apiCode || codeToUse || code || '').trim().toUpperCase().startsWith('PL')
            ? withPlYoaClientOnLine(lineData, apiCode || codeToUse || code, periodScope?.yearOfAssessment)
            : lineData;
          initialData = initialDataFactoryRef.current(factoryLine, initialAdjustmentRef.current || 0);
          if (initialData && typeof initialData === 'object' && !Array.isArray(initialData)) {
            Object.assign(initialData, extractWpMetadata(backendData));
          }
          // 仅 BS-EQ-AL：空壳 GET 可能仍带 adjustments[]，挂到 factory 首行
          if (isFixedScheduleDetailsEmpty
            && String(codeToUse || code || '').toUpperCase() === 'BS-EQ-AL') {
            initialData = mergeEmptyFactoryWithAdjustments(initialData, backendData);
          }
        };

        // ✅ 优先级：
        // - chip-open preferBackend：后端（已成功请求）> 本地草稿兜底 > factory
        // - 受控 / fromPaper / seamless Tab：本地 > 后端 > factory（原行为）
        if (preferBackendOnOpen && didFetchBackend) {
          applyBackendOrFactory();
        } else if (hasValidLocalData) {
          initialData = existingLocalData;
        } else {
          applyBackendOrFactory();
        }

        // MULTI Period Tab：主行空时补 factory 模板（PL + BS investment G1/G2/IOT）
        const codeUpper = String(apiCode || codeToUse || code || '').trim().toUpperCase();
        const isMultiSlice = isMultiSliceSession;
        const isPlMultiSlice = isMultiSlice && codeUpper.startsWith('PL');
        const isBsInvestmentMulti = isMultiSlice && ['BS-NCA-ISUB', 'BS-NCA-IASS', 'BS-CA-IOT'].includes(codeUpper);
        if (
          (isPlMultiSlice || isBsInvestmentMulti)
          && isWpPrimaryRowsEmpty(initialData, periodScope?.yearOfAssessment)
        ) {
          const factoryLine = isPlMultiSlice
            ? withPlYoaClientOnLine(
              lineData,
              apiCode || codeToUse || code,
              periodScope?.yearOfAssessment,
            )
            : lineData;
          const factoryData = initialDataFactoryRef.current(
            factoryLine,
            initialAdjustmentRef.current || 0,
          );
          initialData = seedWpEmptyPrimaryRowsFromFactory(initialData, factoryData);
        }
        // G1/G2/IOT: cost/impairment may be empty while principalSubsidiary has rows — still seed templates
        if (['BS-NCA-ISUB', 'BS-NCA-IASS', 'BS-CA-IOT'].includes(codeUpper)) {
          initialData = ensureCommonInvestmentTemplateRows(
            initialData,
            codeUpper,
            lineData?.amount,
            initialAdjustmentRef.current || 0,
          );
        }
        
        // 3. 设置数据
        const dataToSet = isMultiSliceSession ? deepCloneDetail(initialData) : initialData;

        if (isControlled && setParDetail) {
          // 受控模式下，只有当 parDetail 为空且 unifiedDetail 也为空时才设置初始数据
          if (!currentParDetail && !draftStore.getUnifiedDetail(draftDetailKey)) {
            setParDetail(dataToSet);
          } else if (!currentParDetail && draftStore.getUnifiedDetail(draftDetailKey)) {
            // 如果 parDetail 为空但 unifiedDetail 有数据，同步到 parDetail
            setParDetail(dataToSet);
          } else if (seamlessPeriodSwitch) {
            setParDetail(dataToSet);
          }
        } else {
          setInternalData(dataToSet);
        }

        if (dataToSet != null) {
          draftStore.setUnifiedDetail(
            draftDetailKey,
            isMultiSliceSession ? deepCloneDetail(dataToSet) : dataToSet,
          );
        }
        
        // 4. 设置提取的 ID
        setBackendIds({
          headerId: extractedHeaderId,
          lineId: extractedLineId,
        });
        
        // 标记已初始化
        initCodeRef.current = draftDetailKey;
        hasInitializedRef.current = true;
        // Phase 6: Tab 切换永不把 initialized 打回 false；首开仍走 false→true 龙骨
        setInitialized(true);
        
      } catch (error) {
        console.error('Failed to initialize dialog data:', error);
      }
    })();
  }, [
    open,
    lineData,
    code,
    draftDetailKey,
    periodScope?.sliceKey,
    periodScope?.isFirstSlice,
    periodScope?.yearOfAssessment,
    periodsFromStore,
    effectiveCompanyId,
    annualId,
    isControlled,
    setParDetail,
    workflow,
    backendManagedRows,
    preferBackendData,
    isToPaper,
    isControlled,
    // Removed parDetail and initialAdjustment from dependencies to avoid infinite loops
    // Accessed via refs instead
  ]);
  
  // ✅ 步骤3: 当 initialAdjustment 变化时，更新第一条数据的 adjustment 字段
  // 用于处理 toPaperAdjustments 计算完成后的更新
  const prevInitialAdjustmentRef = useRef<number | undefined>(undefined);
  
  useEffect(() => {
    // 只有 toPaper 才注入 initialAdjustment，fromPaper 不注入
    if (!isToPaper) return;
    // 只有在已初始化且 initialAdjustment 有效变化时才执行
    if (!initialized || !open) return;
    if (initialAdjustment === undefined || initialAdjustment === null) return;
    if (prevInitialAdjustmentRef.current === initialAdjustment) return;
    
    // 记录上一次的值
    prevInitialAdjustmentRef.current = initialAdjustment;
    
    // 获取当前数据
    const draftStore = useAdjustDraftStore.getState();
    const currentDataToUpdate = isControlled 
      ? (parDetail || draftStore.getUnifiedDetail(draftDetailKey))
      : internalData;
    
    if (!currentDataToUpdate) return;
    
    // 检查目标行的 adjustment 是否已经是期望值
    let targetRowAdjustment: number | null = null;
    const readTargetAdj = (commonRow: any[] | undefined) => {
      if (!commonRow || commonRow.length === 0) return null;
      const idx = Math.min(Math.max(adjustmentTargetRowIndex, 0), commonRow.length - 1);
      return commonRow[idx]?.adjustment ?? null;
    };
    if (Array.isArray(currentDataToUpdate) && currentDataToUpdate.length > 0) {
      targetRowAdjustment = readTargetAdj(currentDataToUpdate[0]?.commonRow);
    } else if (currentDataToUpdate?.commonRow) {
      targetRowAdjustment = readTargetAdj(currentDataToUpdate.commonRow);
    }
    
    // 如果已经是期望值，不需要更新
    if (targetRowAdjustment === initialAdjustment) return;
    
    // 注入新的 adjustment 值
    const updatedData = injectAdjustmentToTargetRow(
      currentDataToUpdate,
      initialAdjustment,
      adjustmentTargetRowIndex,
    );
    
    // 更新数据
    if (isControlled && setParDetail) {
      setParDetail(updatedData);
      draftStore.setUnifiedDetail(draftDetailKey, updatedData);
    } else {
      setInternalData(updatedData);
    }
  }, [initialized, open, initialAdjustment, draftDetailKey, isControlled, parDetail, setParDetail, internalData, isToPaper, adjustmentTargetRowIndex]);

  // 获取当前数据
  const currentData = useMemo(() => {
    if (isControlled) {
      return parDetail || getUnifiedDetail(draftDetailKey);
    }
    return internalData;
  }, [isControlled, parDetail, internalData, draftDetailKey, getUnifiedDetail]);
  
  // 更新数据函数
  // 自动保留 'SME-FRS' 字典数据，避免组件 updateData 时丢失
  const updateData = useCallback((newData: any) => {
    let merged = newData;
    // ✅ 自动保留附加元数据（SME-FRS / glInvoiceDetails），避免组件 updateData 时丢失
    if (newData && typeof newData === 'object' && !Array.isArray(newData)) {
      const prev = isControlled ? (parDetail || getUnifiedDetail(draftDetailKey)) : internalData;
      if (prev && typeof prev === 'object') {
        let needsMerge = false;
        const patches: Record<string, any> = {};
        for (const mk of WP_METADATA_ONLY_KEYS) {
          if (prev[mk] !== undefined && newData[mk] === undefined) {
            patches[mk] = prev[mk];
            needsMerge = true;
          }
        }
        if (needsMerge) {
          merged = { ...newData, ...patches };
        }
      }
    }

    // #20: SINGLE 保持原 updateData；仅 MULTI sliceKey 深拷贝并同步草稿，避免 Tab 串引用
    const isMultiSliceSession = !!periodScope?.sliceKey;
    if (isMultiSliceSession) {
      const persisted = deepCloneDetail(merged);
      if (isControlled && setParDetail) {
        setParDetail(persisted);
        setUnifiedDetail(draftDetailKey, deepCloneDetail(persisted));
      } else {
        setInternalData(persisted);
        if (draftDetailKey) {
          setUnifiedDetail(draftDetailKey, deepCloneDetail(persisted));
        }
      }
      // P8：同步写回 byYearOfAssessment 缓存，供全量 batchSave
      const yoa = periodScope?.yearOfAssessment;
      const apiCode = code ? resolveWpFetchCode(code) : code;
      if (yoa && apiCode && effectiveCompanyId > 0 && annualId > 0) {
        const mode = getActiveWpDataMode() !== 'single'
          ? getActiveWpDataMode()
          : resolveWpDataMode(apiCode, 'MULTI');
        if (wpDataModeUsesBatchSave(mode)) {
          upsertWpYoaBucketDetails(effectiveCompanyId, annualId, apiCode, yoa, persisted);
        }
      }
      return;
    }

    if (isControlled && setParDetail) {
      setParDetail(merged);
      setUnifiedDetail(draftDetailKey, merged);
    } else {
      setInternalData(merged);
    }
  }, [isControlled, setParDetail, draftDetailKey, setUnifiedDetail, parDetail, getUnifiedDetail, internalData, periodScope?.sliceKey, periodScope?.yearOfAssessment, code, effectiveCompanyId, annualId]);
  
  return {
    meta,
    lineData,
    currentData,
    updateData,
    initialized,
    backendIds,
    backendListEmpty,
  };
}
